import axios from 'axios'
import iBus from '../config/ibus'
import jwt_decode from 'jwt-decode'
import { accessToken, TranspaisIDToken } from '../utilities/atoms'
import { getGlobalTranspaisIDToken } from '../helpers/pinHelper'
import {
  bookingClientHeaders,
  isLocalBookingApiEnabled,
  resolveBookingAuthToken,
  waitForLocalBookingStub,
} from '../helpers/localBookingApi'
import { getSeatBlockClient } from './seatBlockClient'
import { withFlightHttp } from '../debug/flightHttp'

// const urlBase = process.env.REACT_APP_TRANSPAISWS
const urlBase = process.env.REACT_APP_API_TRANSPAIS
const destinationsUrlBase = process.env.REACT_APP_DESTINATIONS_API // URL base para Destinations API
const appToken = process.env.REACT_APP_TRANSPAISID_TOKEN
const destinations_apikey = process.env.REACT_APP_DESTINATIONS_APIKEY
const debugAppVersion = String(process.env.REACT_APP_DEBUG_APP_VERSION || '').trim()
const appVersion = debugAppVersion || `${process.env.REACT_APP_VERSION}-${process.env.REACT_APP_BUILD}`
const userAgent = `${appVersion} (Kiosk)` // User-Agent para las solicitudes

function getPinFromToken() {
  const token = getGlobalTranspaisIDToken()
  console.debug('Token:', token)
  if (!token) return ''
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.sub || ''
  } catch (e) {
    console.error('Error decoding token for pin:', e)
    return ''
  }
}

function bookingAuthHeaders(extra = {}) {
  const token = resolveBookingAuthToken(getGlobalTranspaisIDToken())
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Application: isLocalBookingApiEnabled() ? 'kiosk' : appToken,
    'x-app-version': userAgent,
    ...bookingClientHeaders(),
    ...extra,
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  return headers
}

/**
 * @param {string} url - URL a la cual consultar.
 * Si la URL comienza con "http://" o "https://", se usa directamente.
 * En caso contrario, se concatena con la constante `urlBase`.
 */
const readUrl = (url = '', base = urlBase) => {
  const pin =  getPinFromToken()
  console.debug('pin for referer:', pin)
  const queryString = `referer=${pin}`
  const fullUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `${base}/${url}`

  return fullUrl.includes('?') ? `${fullUrl}&${queryString}` : `${fullUrl}?${queryString}`
}

/**
 * Función genérica para solicitudes POST.
 */
// const post = (url = '', body = {}, headers = {}, base = urlBase) =>
//   axios.post(readUrl(url, base), body, {
//     headers: {
//       Accept: 'application/json',
//       'Content-Type': 'application/json',
//       Application: appToken,
//       ...headers,
//     },
//     timeout: 60000,
//   })
const post = async (url = '', body = {}, headers = {}, base = urlBase) => {
  if (isLocalBookingApiEnabled() && (!base || base === urlBase)) {
    await waitForLocalBookingStub(getGlobalTranspaisIDToken(), jwt_decode)
  }
  const fullUrl = readUrl(url, base)
  const finalHeaders = bookingAuthHeaders(headers)

  // Depurar los headers
  console.log('POST Request Debug:')
  console.log('URL:', fullUrl)
  console.log('Body:', body)
  console.log('Headers:', finalHeaders)

  return withFlightHttp({
    method: 'POST',
    url,
    fullUrl,
    body,
    run: () =>
      axios.post(fullUrl, body, {
        headers: finalHeaders,
        timeout: 60000,
      }),
  })
}

/**
 * Función genérica para solicitudes GET.
 */
const get = async (url = '', headers = {}, base = urlBase) => {
  const isBookingBase = !base || base === urlBase
  if (isLocalBookingApiEnabled() && isBookingBase) {
    await waitForLocalBookingStub(getGlobalTranspaisIDToken(), jwt_decode)
  }
  const finalUrl = readUrl(url, base)
  // Destinations API uses its own base + keys; only inject booking auth against booking base.
  const finalHeaders = isBookingBase
    ? bookingAuthHeaders(headers)
    : {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...headers,
    }
  const timeout = 60000

  return withFlightHttp({
    method: 'GET',
    url,
    fullUrl: finalUrl,
    run: () =>
      axios.get(finalUrl, {
        headers: finalHeaders,
        timeout,
      }),
  })
}

/**
 * Servicios relacionados con Destinations API.
 */
const destinationsApi = {
  /**
   * Obtener la lista de orígenes.
   */
  origins: () => {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-Api-Key': destinations_apikey,
    }
    return get('api/places', headers, destinationsUrlBase)
      .then((response) => {
        const filteredOrigins = response.data.filter((place) => place.isOrigin)
        return {
          ...response,
          data: {
            ...response.data,
            origins: filteredOrigins,
            all: response.data,
          },
        }
      })
      .catch((error) => {
        console.error('Error al obtener orígenes:', error)
        throw error
      })
  },

  /**
   * Obtener la lista de destinos basados en una lista de IDs.
   */
  destinations: (listTo) => {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-Api-Key': destinations_apikey,
    }

    return get('api/places', headers, destinationsUrlBase)
      .then((response) => {
        const filteredDestinations = response.data.filter((place) => listTo.includes(place._id))
        return {
          ...response,
          data: filteredDestinations,
        }
      })
      .catch((error) => {
        console.error('Error al obtener destinos:', error)
        throw error
      })
  },
}

const search = (origin, origin_name, origin_shortcode, destination, destination_name, destination_shortcode, departure, return_trip = null, fares) => {
  const body = {
    origin,
    origin_name,
    origin_shortcode,
    destination,
    destination_name,
    destination_shortcode,
    departure,
    fares,
  }
  if (return_trip) {
    body.return = return_trip
  }
  return post('v3/booking/trips', body, {}, urlBase) // Elimina el prefijo "/api/transpais"
}

// const bus = (trip_id, key, redondo, punto, usuario) => {
//   const body = {
//     key,
//     redondo,
//     layout: 'horizontal',
//     punto,
//     user: usuario,
//   }

//   return post(`v2/booking/trip/${ trip_id }/bus`, body, {})
// }

// const bus = (session_id, body) => {
//   console.log('body')
//   console.log(body)

//   //SERVICIO FUNCIONAL
//   return axios.post(`/api/transpais/v3/booking/${ session_id }/seats`, body)
//   .then(response => {
//     console.log('seats')
//     console.log(response)
//     return response
//   })
// }
const bus = (session_id, body) => {
  return post(`v3/booking/${session_id}/seats`, body) // Elimina el prefijo "/api/transpais"
}

const seatLive = (session_id, body) => {
  return post(`v3/booking/${session_id}/seats/live`, body) // Elimina el prefijo "/api/transpais"
}

const blockSeat = (session_id, body) => {
  return getSeatBlockClient(post).block(session_id, body)
}

const unblockSeat = (session_id, body) => {
  return getSeatBlockClient(post).unblock(session_id, body)
}

const settings = () => {
  return get('v3/settings')
}

const cart = (session_id, body) => {
  return post(`v3/booking/${session_id}/cart`, body) // Elimina el prefijo "/api/transpais"
}

const fares = () => {
  return get('v3/booking/fares')
}

const block = (accessToken, trip_id, seats, usuario, punto, operation = null) => {
  const body = {
    seats,
    usuario,
    punto
  }

  if (operation) {
    body.operation = operation
  }

  return post(`v2/booking/trip/${ trip_id }/bus/seats/block`, body, {
    Authorization: `Bearer ${ accessToken }`
  })
}

const unblock = (accessToken, trip_id, seats, usuario) => {
  const body = {
    seats,
    user: usuario,
  }

  return post(`v2/booking/trip/${ trip_id }/bus/seats/unlock`, body, {
    Authorization: `Bearer ${ accessToken }`
  })
}

const canReserve = (accessToken, trip_id) => get(`v2/booking/trip/${ trip_id }/bus/seats/validate-reservation/taquilla`,{
  Authorization: `Bearer ${ accessToken }`
})

// const canPay = (accessToken, trip_id) => get(`v2/booking/trip/${ trip_id }/bus/seats/can-pay`,{
//   Authorization: `Bearer ${ accessToken }`
// })
// /api/transpais/v3/booking/trips


const canPay = (session_id, token) => {
  const headers = {
    Accept: 'application/json',
    Authorization: `Bearer ${resolveBookingAuthToken(token)}`,
  }

  return get(`v3/booking/${session_id}/can-pay`, headers)
    .then((response) => {
      return response
    })
    .catch((error) => {
      console.error('Error en el servicio canPay:', error)
      throw error
    })
}

// const confirm = (token, trip_id, usuario, card, email, seats) => {
//   const body = {
//     card,
//     email,
//     seats,
//     usuario,
//   }
//   return post(`v2/booking/trip/${ trip_id }/bus/seats/confirm-creditcard`, body, {
//     Authorization: `Bearer ${ token }`
//   })
// }

const confirm = (session_id, body, token) => {
  const headers = {
    Accept: 'application/json',
    // Authorization: `Bearer ${token}`,
  }
  console.log('confirm')
  console.log('session_id', session_id)
  console.log('body: ',body)
  // return Promise.resolve({ data: dataResponse })
  //FALTA TESTEAR SERVICIO
  return post(`v3/booking/${session_id}/confirm`, body, headers)
  // return axios.post(`/api/transpais/v3/booking/${ session_id }/confirm`, body)
  // .then(response => {
  //   console.log('confirm')
  //   console.log(response)
  //   return response
  // })
}

const credentials = (token) => {
  const headers = {
    Accept: 'application/json',
    Authorization: `Bearer ${resolveBookingAuthToken(token)}`,
  }

  console.log('Servicio credentials:')
  console.log('URL:', `v3/payments/mit`)
  console.log('Headers:', headers)

  return get(`v3/payments/mit`, headers)
    .then((response) => {
      console.log('credentials')
      console.log(response)
      return response
    })
    .catch((error) => {
      console.error('Error en el servicio credentials:', error)
      throw error
    })
}

const tickets = (reservation, pin) => {
  const body = {
    pin
  }
  return post(`/v2/tools/tickets/by-reservation/${reservation}`, body, {})
}

const booking = (token, trip_id, tipo) => get(`v2/booking/trip/${ trip_id }/bus/seats/validate-reservation/${ tipo }`, {
  Authorization: `Bearer ${ token }`
})

const confirmReservation = (token, trip_id, email, seats) => {
  const body = {
    email,
    seats,
  }
  return post(`v2/booking/trip/${trip_id}/bus/seats/confirm-reservation-cash`, body, {
    Authorization: `Bearer ${ token }`
  })
}

// const ticketsAdmin = (page) => get (`/v2/accounts/my-tickets`,{
//   Authorization: `Bearer ${ accessToken }`
// })

const ticketsAdmin = (page) => {
  if (!page) {
    page = 1
  }
  const transpaisToken = getGlobalTranspaisIDToken()
  const headers = {
    Authorization: `Bearer ${transpaisToken}`
  }
  return get(`v3/tickets?page=${page}`, headers)
}

const transpais = {
  // origins,
  // destinations,
  ...destinationsApi,
  search,
  bus,
  block,
  unblock,
  credentials,
  confirm,
  tickets,
  booking,
  confirmReservation,
  canPay,
  canReserve,
  ticketsAdmin,
  cart,
  fares,
  blockSeat,
  unblockSeat,
  seatLive,
  settings
}

export default transpais
